import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Pictures on assignments (supabase/migrations/20261033_assignment_images.sql).
// The teacher's photos are shrunk in the browser first (1600 px on the long
// side, JPEG), so a phone picture uploads quickly and loads quickly for
// students; GIFs go up as they are, to keep their animation. The bucket is
// private, so pages show them through signed links that last an hour.
// ─────────────────────────────────────────────────────────────────────────────

export type AssignmentImage = { path: string; caption?: string; w?: number; h?: number };
export const MAX_IMAGES = 8;
const BUCKET = 'assignment-images';
const MAX_SIDE = 1600;

/** Shrink a picture to a JPEG (or keep a GIF as it is). */
async function shrink(file: File): Promise<{ blob: Blob; type: string; ext: string; w: number; h: number }> {
  if (file.type === 'image/gif') {
    const bmp = await createImageBitmap(file).catch(() => null);
    return { blob: file, type: 'image/gif', ext: 'gif', w: bmp?.width ?? 0, h: bmp?.height ?? 0 };
  }
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * k), h = Math.round(bmp.height * k);
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const g = c.getContext('2d')!;
  g.fillStyle = '#fff'; g.fillRect(0, 0, w, h);        // a see-through PNG gets a white background
  g.drawImage(bmp, 0, 0, w, h);
  const blob = await new Promise<Blob>((ok, no) => c.toBlob(b => (b ? ok(b) : no(new Error('Could not read that picture.'))), 'image/jpeg', 0.85));
  return { blob, type: 'image/jpeg', ext: 'jpg', w, h };
}

export async function uploadImage(classroomId: string, file: File): Promise<AssignmentImage> {
  if (!file.type.startsWith('image/')) throw new Error(`“${file.name}” isn’t a picture.`);
  if (file.size > 25 * 1024 * 1024) throw new Error(`“${file.name}” is too big (25 MB at most).`);
  const { blob, type, ext, w, h } = await shrink(file);
  if (blob.size > 5 * 1024 * 1024) throw new Error(`“${file.name}” is still too big after shrinking (GIFs: 5 MB at most).`);
  const path = `${classroomId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { contentType: type, cacheControl: '31536000' });
  if (error) throw new Error(error.message);
  return { path, w, h };
}

export async function removeImages(paths: string[]): Promise<void> {
  if (paths.length) await supabase.storage.from(BUCKET).remove(paths);
}

// Signed links, kept for most of their hour so a page doesn't ask twice.
const links = new Map<string, { url: string; until: number }>();
export async function imageUrls(paths: string[]): Promise<Record<string, string>> {
  const now = Date.now(), out: Record<string, string> = {};
  const need = paths.filter(p => { const l = links.get(p); if (l && l.until > now) { out[p] = l.url; return false; } return true; });
  if (need.length) {
    const { data } = await supabase.storage.from(BUCKET).createSignedUrls(need, 3600);
    for (const d of data ?? []) if (d.signedUrl && d.path) { out[d.path] = d.signedUrl; links.set(d.path, { url: d.signedUrl, until: now + 50 * 60_000 }); }
  }
  return out;
}

export const cleanImages = (x: unknown): AssignmentImage[] =>
  (Array.isArray(x) ? x : []).filter((i): i is AssignmentImage => !!i && typeof i === 'object' && typeof (i as AssignmentImage).path === 'string')
    .slice(0, MAX_IMAGES)
    .map(i => ({ path: i.path, ...(i.caption ? { caption: String(i.caption).slice(0, 200) } : {}), ...(i.w ? { w: i.w, h: i.h } : {}) }));
